import type { LectureSnapshot } from '@wx-weiqi/contracts';
import {
  boardCoordinate,
  boardPadding,
  starPoints,
  stoneRadius,
} from '../../domain/board-geometry';
import { ProblemAnnotationLayer } from '../ProblemAnnotationLayer';
export function LectureDiagram({
  snapshot: s,
  id = 'record',
}: {
  snapshot: LectureSnapshot;
  id?: string;
}) {
  const p = boardPadding(s.size),
    r = stoneRadius(s.size),
    xy = (n: number) => boardCoordinate(s.size, n),
    indices = Array.from({ length: s.size }, (_, i) => i);
  return (
    <svg
      viewBox="0 0 1000 1000"
      role="img"
      aria-label={`完整${s.size}路记录棋图`}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        fontFamily: 'Arial, PingFang SC, Microsoft YaHei, sans-serif',
      }}
    >
      <defs>
        <linearGradient id={`${id}-wood`} x2="0.8" y2="1">
          <stop stopColor="#ead09c" />
          <stop offset="1" stopColor="#d7af71" />
        </linearGradient>
        <radialGradient id={`${id}-white`} cx="32%" cy="27%" r="75%">
          <stop stopColor="#fffdf3" />
          <stop offset=".7" stopColor="#eee9dc" />
          <stop offset="1" stopColor="#c6bdab" />
        </radialGradient>
        <radialGradient id={`${id}-black`} cx="32%" cy="24%" r="80%">
          <stop stopColor="#54534f" />
          <stop offset=".5" stopColor="#292925" />
          <stop offset="1" stopColor="#131411" />
        </radialGradient>
      </defs>
      <rect width="1000" height="1000" rx="12" fill={`url(#${id}-wood)`} />
      <g stroke="#6d532f" strokeWidth="1.5">
        {indices.map((i) => (
          <path key={i} d={`M ${p} ${xy(i)} H ${1000 - p} M ${xy(i)} ${p} V ${1000 - p}`} />
        ))}
      </g>
      <g fill="#4a3c27" fontSize="20" textAnchor="middle" dominantBaseline="central">
        {indices.map((i) => (
          <g key={i}>
            <text x={xy(i)} y={16}>
              {'ABCDEFGHJKLMNOPQRST'[i]}
            </text>
            <text x={16} y={xy(i)}>
              {s.size - i}
            </text>
          </g>
        ))}
      </g>
      <g fill="#5e4829">
        {starPoints(s.size).map((pt) => (
          <circle key={`${pt.x}-${pt.y}`} cx={xy(pt.x)} cy={xy(pt.y)} r="4" />
        ))}
      </g>
      {s.stones.map((stone, i) =>
        stone ? (
          <g key={i} data-point={`${i % s.size}:${Math.floor(i / s.size)}`} data-stone={stone}>
            <circle
              cx={xy(i % s.size) + 1.5}
              cy={xy(Math.floor(i / s.size)) + 2}
              r={r}
              fill="#49351b"
              opacity=".2"
            />
            <circle
              cx={xy(i % s.size)}
              cy={xy(Math.floor(i / s.size))}
              r={r}
              fill={`url(#${id}-${stone === 'BLACK' ? 'black' : 'white'})`}
              stroke={stone === 'BLACK' ? '#171814' : '#554c40'}
              strokeWidth="1.5"
            />
          </g>
        ) : null,
      )}
      {s.labels.map((l) => (
        <text
          key={`${l.point.x}:${l.point.y}`}
          x={xy(l.point.x)}
          y={xy(l.point.y)}
          textAnchor="middle"
          dominantBaseline="central"
          fill={l.color === 'BLACK' ? '#fff9e9' : '#252a25'}
          fontWeight="600"
          fontSize={r * (l.label.length > 2 ? 1.05 : 1.3)}
        >
          {l.label}
        </text>
      ))}
      <ProblemAnnotationLayer size={s.size} stones={s.stones} annotations={s.initialAnnotations} />
      {s.annotations.map((a, i) =>
        a.kind === 'BRUSH' ? (
          <polyline
            key={i}
            points={a.points.map((pt) => `${pt.x},${pt.y}`).join(' ')}
            fill="none"
            stroke={a.color === 'RED' ? '#d43a31' : '#1768d7'}
            strokeWidth={a.width}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <text
            key={i}
            x={xy(a.point.x)}
            y={xy(a.point.y)}
            textAnchor="middle"
            dominantBaseline="central"
            fill={a.color === 'RED' ? '#d43a31' : '#1768d7'}
            fontSize="40"
            fontWeight="700"
          >
            {a.kind === 'LETTER'
              ? (a.label ?? 'A')
              : a.kind === 'TRIANGLE'
                ? '△'
                : a.kind === 'CIRCLE'
                  ? '〇'
                  : '×'}
          </text>
        ),
      )}
    </svg>
  );
}
